<template>
  <main>
    <div class="menu-area">
      <div class="space-left"></div>
      <div class="menu-form">
        <h1>Weekly Menu</h1>
        <div class="form-container">
          <div class="dish-container" id="week">
            <!-- Loop through 7 days -->
            <div class="weekday" v-for="(dish, index) in weeklyMenu" :key="index">
              <h3>
                {{ weekdays[index] }}
                ({{ formatDate(nextMondayDates[index]) }})
              </h3>
              <div class="dish-row">
                <!-- Added class "dish-name" and title attribute for tooltip -->
                <p
                  id="dish"
                  class="dish-name"
                  :class="{ clickable: !!dish }"
                  :title="dish?.Name"
                  @click="dish ? showDishDetails(dish) : null"
                >
                  {{ dish?.Name || "No dish selected" }}
                </p>
                <button class="lock-button" @click="toggleLock(index)">
                  <span v-if="lockedDishes[index]">
                    <!-- Inline SVG for locked icon -->
                    <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                      <title>Locked</title>
                      <path d="M16,9.5V6A4,4,0,0,0,8,6V9.5A2.5,2.5,0,0,0,5.5,12v7A2.5,2.5,0,0,0,8,21.5h8A2.5,2.5,0,0,0,18.5,19V12A2.5,2.5,0,0,0,16,9.5Zm-3,7.22V17a1,1,0,0,1-2,0v-.28A2,2,0,0,1,10,15a2,2,0,0,1,4,0A2,2,0,0,1,13,16.72ZM14,9.5H10V6a2,2,0,0,1,4,0Z" fill="#464646"/>
                    </svg>
                  </span>
                  <span v-else>
                    <!-- Inline SVG for unlocked icon -->
                    <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
                      <title>Unlocked</title>
                      <path d="M16,9.5V6A4,4,0,0,0,8,6a1,1,0,0,0,2,0,2,2,0,0,1,4,0V9.5H8A2.5,2.5,0,0,0,5.5,12v7A2.5,2.5,0,0,0,8,21.5h8A2.5,2.5,0,0,0,18.5,19V12A2.5,2.5,0,0,0,16,9.5Zm-3,7.22V17a1,1,0,0,1-2,0v-.28A2,2,0,0,1,10,15a2,2,0,0,1,4,0A2,2,0,0,1,13,16.72Z" fill="#464646"/>
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </div>
          <div class="buttons">
            <button class="button" @click="generateMenu">Generate New Menu</button>
            <!-- On mobile/desktop, only show Save/Export button if authenticated -->
            <button class="button save-button" @click="openConfirmModal">
              Save/Export
            </button>
            <button class="button reset-button" @click="reset">Reset</button>
          </div>
        </div>
      </div>
      <div class="space-right"></div>
    </div>

    <!-- Dish Details Modal -->
    <div v-if="showModal" class="modal">
      <div class="modal-content">
        <h2>{{ selectedDish?.Name || "Dish Details" }}</h2>
        <p><strong>Main Ingredient:</strong> {{ selectedDish?.MainIngredient || "Unknown" }}</p>
        <p><strong>Category:</strong> {{ selectedDish?.Category || "Unknown" }}</p>
        <p v-if="safeUrl(selectedDish.RecipeUrl)">
          <strong>Recipe:</strong>
          <a :href="safeUrl(selectedDish.RecipeUrl)" target="_blank" rel="noopener noreferrer" class="recipe-link">
            {{ selectedDish.RecipeUrl }}
          </a>
        </p>
        <!-- Display Sides if available -->
        <p v-if="selectedDish?.sides">
          <strong>Sides:</strong><br />
          {{ selectedDish.sides }}
        </p>
        <button class="button" @click="closeDetailModal">Close</button>
      </div>
    </div>

      <!-- Confirm Save Modal -->
      <div v-if="confirmModalVisible" class="modal">
      <div class="modal-content">
        <h2>Confirm Save</h2>
        <p>
          Do you want to store these dishes for the selected dates and proceed to calendar export?
          Your dishes will be saved with new "Last Eaten" dates.
        </p>
        <button class="button primary-button" @click="confirmSave">
          Yes, Save & Export
        </button>
        <button class="button cancel-button" @click="closeDetailModal">
          Cancel
        </button>
      </div>
    </div>
      
      <!-- Saving Menu Modal -->
      <div v-if="savingMenu" class="modal">
      <div class="modal-content">
        <div class="spinner"></div>
        <p>Saving menu...</p>
      </div>
    </div>

    <!-- Export Confirmation Modal -->
    <div v-if="exportModalVisible" class="modal">
      <div class="modal-content">
        <h2>Export Menu to Calendar</h2>
        <!-- Updated preview using a table for alignment -->
        <div class="export-menu-preview">
          <table class="export-menu-table">
            <tbody>
              <tr v-for="(dish, index) in weeklyMenu" :key="index">
                <td class="day-col">{{ weekdays[index] }}</td>
                <td class="separator-col">:</td>
                <td class="dish-col">{{ dish?.Name || "No dish selected" }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-if="!exportCompleted">
          Would you like to download this week's menu as a calendar file?
        </p>
        <p v-else>Export completed. You can now close this modal.</p>
        <button v-if="!exportCompleted" class="button" @click="handleExportCalendar">
          Export
        </button>
        <button class="button cancel-button" @click="closeExportModal">
          {{ exportCompleted ? "Close" : "Cancel" }}
        </button>
      </div>
    </div>

    <!-- Warning Message (if settings cannot be honored) -->
    <div v-if="warningMessage" class="warning-message">
      <span>{{ warningMessage }}</span>
      <button class="warning-close" @click="warningMessage = ''">×</button>
    </div>
  </main>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { auth } from "@/services/firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  listDishes,
  fetchOrCreateUserSettings,
  saveUserDishes,
  fetchDefaultDishes
} from "@/services/firebaseFunctions";
import { generateICSFile } from "@/services/icsGenerator";
import { useNotification } from "@/services/notificationService";
import { safeUrl } from "@/services/safeUrl";

export default {
  name: "MenuView",
  setup() {
    // Weekly menu state
    const weeklyMenu = ref(new Array(7).fill(null));
    const lockedDishes = ref(new Array(7).fill(false));
    const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
    const detailEditMode = ref(false);
    const user = ref(null);
    const isAuthenticated = computed(() => !!user.value);

    // Next Monday dates computed array
    const nextMondayRef = ref(null);
    const nextMondayDates = computed(() => {
      if (!nextMondayRef.value) return [];
      const dates = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(nextMondayRef.value);
        d.setDate(nextMondayRef.value.getDate() + i);
        dates.push(d);
      }
      return dates;
    });
    const formatDate = date => (date ? date.toLocaleDateString() : "");

    // Modal and notification states
    const showModal = ref(false);
    const savingMenu = ref(false);
    const exportModalVisible = ref(false);
    const exportCompleted = ref(false);
    const selectedDish = ref(null);
    const warningMessage = ref("");
    const confirmModalVisible = ref(false);

    let unsubscribeAuth = null;
    onMounted(() => {
      unsubscribeAuth = onAuthStateChanged(auth, (firebaseUser) => {
        user.value = firebaseUser; // user.value is now reactive
      });

      const today = new Date();
      const daysUntilMonday = (8 - today.getDay()) % 7 || 7;
      const nextMonday = new Date(today);
      nextMonday.setDate(today.getDate() + daysUntilMonday);
      nextMondayRef.value = nextMonday;
    });

    onUnmounted(() => {
      if (unsubscribeAuth) unsubscribeAuth();
    });

    const closeDetailModal = () => {
      showModal.value = false;
      selectedDish.value = {};
      detailEditMode.value = false;
      confirmModalVisible.value = false;
    };

    const toggleLock = index => {
      lockedDishes.value[index] = !lockedDishes.value[index];
    };

    // Dishes from Firestore/default
    const userDishes = ref([]);
    const { displayNotification } = useNotification();

    // Shuffle function (Fisher–Yates)
    const shuffle = array => {
      let arr = [...array];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    // Generate Menu Function
    const generateMenu = async () => {
      try {
        if (!isAuthenticated.value) {
          const defaultDishes = await fetchDefaultDishes();
          const weekdayDishes = defaultDishes.filter(dish => dish.Category === "Everyday");
          const weekendDishes = defaultDishes.filter(dish => dish.Category === "Weekend");

          weeklyMenu.value = weeklyMenu.value.map((dish, index) => {
            if (lockedDishes.value[index]) return dish;
            return index < 5 ? shuffle(weekdayDishes)[0] : shuffle(weekendDishes)[0];
          });
          warningMessage.value = "";
          return;
        }

        userDishes.value = await listDishes();
        const userSettings = await fetchOrCreateUserSettings();

        // Filter eligible dishes based on LastEaten and user settings
        const eligibleDishes = userDishes.value.filter(dish => {
          if (!dish.LastEaten) return true;
          const lastEatenDate = new Date(dish.LastEaten);
          const daysSinceEaten = (new Date() - lastEatenDate) / (1000 * 60 * 60 * 24);
          return daysSinceEaten >= userSettings.daysBetween;
        });

        const weekdayDishes = eligibleDishes.filter(dish => dish.Category === "Everyday");
        const weekendDishes = eligibleDishes.filter(dish => dish.Category === "Weekend");

        let chosenIds = [];
        let warning = "";

        // Use different dish selection logic based on preference mode
        if (userSettings.preferenceMode === 'weekly') {
          // Weekly schedule mode
          weeklyMenu.value = weeklyMenu.value.map((dish, index) => {
            if (lockedDishes.value[index]) {
              if (dish && dish.id) chosenIds.push(dish.id);
              return dish;
            }

            // Get the preferred main ingredient for this day
            const dayPreference = userSettings.weeklySchedule[index].mainIngredient;
            
            // Determine the pool of dishes to choose from
            let candidates = index < 5 ? weekdayDishes : weekendDishes;
            
            // Filter by main ingredient if not "surprise"
            if (dayPreference !== "surprise" && dayPreference !== "none") {
              const filteredByIngredient = candidates.filter(
                d => d.MainIngredient === dayPreference && !chosenIds.includes(d.id)
              );
              
              if (filteredByIngredient.length > 0) {
                candidates = filteredByIngredient;
              } else {
                // Fallback if no dishes match the ingredient
                warning = "Warning: Not enough eligible dishes to match your preferred ingredients. Some days may use different ingredients.";
              }
            }
            
            // Skip this day if preference is "none"
            if (dayPreference === "none") {
              return null;
            }
            
            // Ensure we don't choose duplicates if possible
            let available = candidates.filter(d => !chosenIds.includes(d.id));
            
            if (available.length === 0) {
              available = candidates;
              if (!warning) {
                warning = "Warning: Not enough eligible dishes to avoid repeats. Some dishes may appear multiple times.";
              }
            }
            
            let chosen = shuffle(available)[0];
            if (chosen) chosenIds.push(chosen.id);
            return chosen;
          });
        } else {
          // Original distribution mode
          // Count how many of each main ingredient we need
          const ingredientCounts = {};
          userSettings.mainIngredientPreferences.forEach(pref => {
            ingredientCounts[pref.name] = pref.days;
          });
          
          // Fill the menu day by day
          weeklyMenu.value = weeklyMenu.value.map((dish, index) => {
            if (lockedDishes.value[index]) {
              // If dish is locked, update ingredient counts
              if (dish && dish.MainIngredient) {
                ingredientCounts[dish.MainIngredient] = Math.max(0, (ingredientCounts[dish.MainIngredient] || 0) - 1);
              }
              if (dish && dish.id) chosenIds.push(dish.id);
              return dish;
            }

            // Get base candidates for the day type
            let candidates = index < 5 ? weekdayDishes : weekendDishes;
            
            // First try to honor ingredient preferences
            let availableIngredients = Object.keys(ingredientCounts)
              .filter(ing => ingredientCounts[ing] > 0);
            
            let chosen = null;
            
            if (availableIngredients.length > 0) {
              // Try to find dishes with the needed ingredients
              for (let ingredient of availableIngredients) {
                const dishesWithIngredient = candidates.filter(
                  d => d.MainIngredient === ingredient && !chosenIds.includes(d.id)
                );
                
                if (dishesWithIngredient.length > 0) {
                  chosen = shuffle(dishesWithIngredient)[0];
                  ingredientCounts[ingredient]--;
                  break;
                }
              }
            }
            
            // If we couldn't honor preferences, just pick any available dish
            if (!chosen) {
              warning = "Warning: Not enough eligible dishes to honor your ingredient preferences.";
              
              // Try to avoid repeats if possible
              let available = candidates.filter(d => !chosenIds.includes(d.id));
              
              if (available.length === 0) {
                available = candidates;
                if (!warning) {
                  warning = "Warning: Not enough eligible dishes to avoid repeats.";
                }
              }
              
              chosen = shuffle(available)[0];
            }
            
            if (chosen) chosenIds.push(chosen.id);
            return chosen;
          });
        }
        
        warningMessage.value = warning;
      } catch (error) {
        console.error("Error generating menu:", error.message);
      }
    };

    // Confirm Save Modal functions
    const openConfirmModal = () => {
      if (!isAuthenticated.value) {
        displayNotification("You need to log in to access this functionality.");
        return;
      }
      confirmModalVisible.value = true;
    };

    const confirmSave = async () => {
      confirmModalVisible.value = false;
      savingMenu.value = true;
      try {
        // Update LastEaten for each dish based on next Monday dates
        for (let i = 0; i < 7; i++) {
          const dish = weeklyMenu.value[i];
          if (!dish) continue;
          const dishDate = new Date(nextMondayRef.value);
          dishDate.setDate(nextMondayRef.value.getDate() + i);
          dish.LastEaten = dishDate.toISOString();
        }
        const updatedDishes = weeklyMenu.value.filter(Boolean);
        await saveUserDishes(updatedDishes);
        savingMenu.value = false;
        exportModalVisible.value = true;
      } catch (error) {
        console.error("Error saving menu:", error.message);
        displayNotification("Error saving menu: " + error.message);
        savingMenu.value = false;
      }
    };

    // Save menu: if authenticated, show confirm modal; if not, notify login required
    const saveMenu = async () => {
      if (!isAuthenticated.value) {
        displayNotification("You need to log in to access this functionality.");
        return;
      }
      openConfirmModal();
    };

    // Export calendar
    const handleExportCalendar = () => {
      generateICSFile(weeklyMenu.value, weekdays);
      exportCompleted.value = true;
    };

    const closeExportModal = () => {
      exportModalVisible.value = false;
      exportCompleted.value = false;
    };

    // Reset menu
    const reset = () => {
      weeklyMenu.value = new Array(7).fill(null);
      lockedDishes.value = new Array(7).fill(false);
      warningMessage.value = "";
    };

    // Show dish details modal
    const showDishDetails = dish => {
      if (!dish) return;
      selectedDish.value = dish;
      showModal.value = true;
    };

    return {
      weeklyMenu,
      lockedDishes,
      weekdays,
      nextMondayDates,
      formatDate,
      generateMenu,
      toggleLock,
      saveMenu,
      confirmSave,
      openConfirmModal,
      handleExportCalendar,
      closeExportModal,
      reset,
      showDishDetails,
      showModal,
      savingMenu,
      exportModalVisible,
      exportCompleted,
      selectedDish,
      warningMessage,
      confirmModalVisible,
      isAuthenticated,
      closeDetailModal,
      safeUrl,
    };
  },
};
</script>

<style scoped>
.menu-area {
  display: flex;
}

.space-left,
.space-right {
  flex: 1;
}

.menu-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  color: var(--text-primary);
  background: rgba(0, 0, 0, 0.85);
  border-radius: 15px;
  padding: 3rem;
}

.menu-form h1 {
  margin-bottom: 1.5rem;
  color: var(--text-secondary);
}

/* Dish Container / Weekdays */
.dish-container div {
  border-bottom: 1px solid var(--text-primary);
  line-height: 1.5em;
}

.weekday {
  margin-top: 10px;
  margin-bottom: 10px;
}

.weekday h3 {
  margin-bottom: 6px;
  color: var(--text-secondary);
  font-size: 1.2rem;
}

.dish-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dish-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

#dish {
  margin-right: 10px;
}

.clickable {
  cursor: pointer;
  text-decoration: underline;
}

/* Lock Button */
.lock-button {
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: color 0.2s;
}

.lock-button.locked {
  color: #ff4d4d;
}

.lock-button:hover {
  text-decoration: underline;
}

/* Buttons container */
.buttons {
  margin-top: 20px;
  display: flex;
  gap: 10px;
}

.button {
  padding: 8px 14px;
  font-size: 14px;
  background-color: #2a2a2a;
  color: #ececec;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.button:hover {
  background-color: #333333;
}

.save-button {
  background-color: #3a8ef7;
}

.save-button:hover {
  background-color: #2a78d6;
}

.reset-button {
  background-color: #b31c1c;
}

.reset-button:hover {
  background-color: #9a1515;
}

.recipe-link {
  color: #ffc107;
  text-decoration: none;
}

.recipe-link:hover {
  text-decoration: underline;
}

/* Export Modal Preview (using a table for alignment) */
.export-menu-preview {
  margin-bottom: 20px;
  width: 100%;
}
.export-menu-table {
  width: 100%;
  border-collapse: collapse;
}
.export-menu-table td {
  padding: 5px 0;
  vertical-align: top;
}
.day-col {
  width: 100px;
  font-weight: bold;
  color: var(--text-secondary);
}
.separator-col {
  width: 10px;
  text-align: center;
  color: var(--text-primary);
}
.dish-col {
  color: var(--text-primary);
  /* Allow wrapping for longer dish names: */
  white-space: normal;
  word-wrap: break-word;
}

/* Modals */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: #1e1e1e;
  padding: 20px;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  text-align: center;
  color: var(--text-primary);
}

.modal-content h2 {
  margin-bottom: 20px;
  color: var(--text-secondary);
}

.modal-content p {
  margin: 10px 0;
}

.modal-content a {
  color: #4caf50;
  text-decoration: none;
}

.modal-content a:hover {
  text-decoration: underline;
}

/* Spinner */
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid #ffffff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 15px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Warning Message */
.warning-message {
  background-color: rgba(0, 0, 0, 0.8);
  border: 1px solid #ff4d4d;
  color: #ff4d4d;
  padding: 8px 12px;
  border-radius: 5px;
  font-size: 0.9em;
  margin: 15px auto;
  max-width: 500px;
  text-align: center;
  position: relative;
}

.warning-close {
  position: absolute;
  top: 2px;
  right: 4px;
  background: transparent;
  border: none;
  color: #ff4d4d;
  font-size: 1.2em;
  cursor: pointer;
}

/* Mobile adjustments */
@media (max-width: 768px) {
  .menu-area {
    flex-direction: column;
    padding: 10px;
  }
  .space-left,
  .space-right {
    display: none;
  }
  .menu-form {
    width: 100%;
    padding: 1.5rem;
    border-radius: 10px;
  }
  .menu-form h1 {
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }
  .weekday h3 {
    font-size: 0.9rem;
    margin-top: 0.3rem;
    margin-bottom: 0.3rem;
  }
  .modal-content {
    max-width: 90%;
    padding: 15px;
  }
  .button {
    padding: 6px 12px;
    font-size: 13px;
  }
  .warning-message {
    position: fixed;
    top: 0;
    left: 10px;
    right: 10px;
    margin: 0 auto;
    z-index: 1100;
    font-size: 0.95em;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
  .warning-close {
    position: absolute;
    top: 2px;
    right: 4px;
    background: transparent;
    border: none;
    color: #ff4d4d;
    font-size: 1.2em;
    cursor: pointer;
  }

  /* Make day column a bit smaller for mobile */
  .day-col {
    width: 70px;
  }
  .separator-col {
    width: 5px;
  }
}
</style>
